import { App } from "antd";
import { useState } from "react";
import { UserRole } from "helpers/constants/users";
import {
  useCreateUserApiV1KeycloakAdminUsersPost,
  useDeleteUserApiV1KeycloakAdminUsersUserIdDelete,
  useResetPasswordApiV1KeycloakAdminUsersUserIdResetPasswordPost,
} from "vl-api/vlComponents";
import styles from "./style.module.scss";

type CreateUserData = {
  username: string;
  firstName: string;
  lastName: string;
  password: string;
  enabled?: boolean;
  workspace_role?: "admin" | "editor" | "viewer";
};

type ChangeRoleData = {
  userId: string;
  role: string;
};

type ResetPasswordData = {
  userId: string;
  password: string;
};

type DeleteUserData = {
  userId: string;
};

export type ConfirmAdminRoleChangeProps = {
  values: {
    role: UserRole;
  };
  performUpdate: () => void | Promise<void>;
  onCancel: () => void;
};

type UseUserModalActionsProps = {
  onRefresh?: () => void;
};

export const useUserModalActions = ({
  onRefresh,
}: UseUserModalActionsProps = {}) => {
  const { modal, notification } = App.useApp();
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);

  const createUserMutation = useCreateUserApiV1KeycloakAdminUsersPost({
    onSuccess: () => {
      notification.success({ message: "User created successfully" });
      onRefresh?.();
    },
  });

  const deleteMutation = useDeleteUserApiV1KeycloakAdminUsersUserIdDelete();
  const resetPasswordMutation =
    useResetPasswordApiV1KeycloakAdminUsersUserIdResetPasswordPost();

  const createUser = async (userData: CreateUserData) => {
    try {
      await createUserMutation.mutateAsync({
        body: {
          username: userData.username,
          password: userData.password,
          firstName: userData.firstName,
          lastName: userData.lastName,
          enabled: userData.enabled ?? true,
          workspace_role: userData.workspace_role ?? "viewer",
        },
      });
    } catch (err: any) {
      notification.error({
        message: "Failed to create user",
        description: err?.detail || err?.message || "Unknown error",
      });
      throw err;
    }
  };

  const changeUserRole = async (data: ChangeRoleData) => {
    setIsUpdatingRole(true);
    try {
      const response = await fetch(
        `/api/v1/workspace/members/${data.userId}/role`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ role: data.role.toLowerCase() }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Failed to update role: ${response.status}`,
        );
      }

      // When promoting someone to admin, the current user (old admin) gets
      // demoted and their session is invalidated by the backend — force logout.
      if (data.role.toLowerCase() === "admin") {
        notification.success({
          message: "Admin role transferred — logging you out",
        });
        setTimeout(() => {
          window.location.href = "/api/v1/oidc/logout";
        }, 1000);
        return;
      }

      onRefresh?.();
      notification.success({ message: "User role updated successfully" });
    } catch (err: any) {
      notification.error({
        message: "Failed to update user role",
        description: err?.detail || err?.message || String(err),
      });
      throw err;
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const resetUserPassword = async (data: ResetPasswordData) => {
    try {
      await resetPasswordMutation.mutateAsync({
        pathParams: { userId: data.userId },
        body: { password: data.password },
      });
      notification.success({ message: "Password reset successfully" });
    } catch (err: any) {
      notification.error({
        message: "Failed to reset password",
        description: err?.detail || err?.message || String(err),
      });
      throw err;
    }
  };

  const deleteUser = async (data: DeleteUserData) => {
    try {
      await deleteMutation.mutateAsync({
        pathParams: { userId: data.userId },
      });
      onRefresh?.();
      notification.success({ message: "User deleted successfully" });
    } catch (err: any) {
      notification.error({
        message: "Failed to delete user",
        description: err?.detail || err?.message || String(err),
      });
      throw err;
    }
  };

  const confirmAdminRoleChange = (props: ConfirmAdminRoleChangeProps): void => {
    const { values, performUpdate, onCancel } = props;
    if (values.role !== UserRole.Admin) {
      return;
    }

    modal.confirm({
      title: (
        <span className={styles.modalText}>
          This workspace supports a single Admin.
        </span>
      ),
      content: (
        <span className={styles.modalText}>
          Confirming will change your role to Editor, and this could only be
          reverted by the new admin.
        </span>
      ),

      onOk: performUpdate,
      onCancel: onCancel,
      okText: "Confirm",
      cancelText: "Back",
      className: styles.changeAdminModal,
      okButtonProps: { size: "middle" },
      cancelButtonProps: {
        size: "middle",
        title: "Back",
      },
      autoFocusButton: null,
    });
  };

  const handleRoleChange = async (
    user: { id: string; username: string; currentRole: UserRole },
    newRole: UserRole,
    onUpdate: (role: UserRole) => void,
  ) => {
    if (newRole === user.currentRole) return;

    const performUpdate = async () => {
      try {
        await changeUserRole({
          userId: user.id,
          role: newRole,
        });
        onUpdate(newRole);
        notification.success({
          message: "Role Updated",
          description: `Role has been changed to ${newRole}.`,
        });
      } catch (error) {
        notification.error({
          message: "Update Failed",
          description: "There was an error updating the user role.",
        });
      }
    };

    if (newRole === UserRole.Admin) {
      confirmAdminRoleChange({
        values: { role: newRole },
        performUpdate,
        onCancel: () => {},
      });
    } else {
      modal.confirm({
        title: "Confirm Role Change",
        content: `Are you sure you want to change role from ${user.currentRole} to ${newRole}?`,
        onOk: performUpdate,
        okText: "Confirm",
        cancelText: "Cancel",
      });
    }
  };

  return {
    createUser,
    changeUserRole,
    resetUserPassword,
    deleteUser,
    confirmAdminRoleChange,
    handleRoleChange,
    loading:
      createUserMutation.isPending ||
      deleteMutation.isPending ||
      resetPasswordMutation.isPending ||
      isUpdatingRole,
  };
};
